// src/components/PWAInstallGuide.js (모바일 전용 최종 버전)
import React, { useState, useEffect } from 'react';

function PWAInstallGuide({ darkMode }) {
  const [showGuide, setShowGuide] = useState(false);
  const [deviceType, setDeviceType] = useState('unknown');
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // 기기 및 브라우저 판별
    const userAgent = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    const isChrome = /Chrome/.test(userAgent) && !/Edge/.test(userAgent);
    const isDesktop = !isMobile;

    // 기기 타입 설정
    if (isIOS) {
      setDeviceType('ios');
    } else if (isAndroid) {
      setDeviceType('android');
    } else if (isDesktop && isChrome) {
      setDeviceType('desktop-chrome');
    } else if (isDesktop) {
      setDeviceType('desktop-other');
    }

    // PWA 설치 가능 여부 확인
    const checkInstallable = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = window.navigator.standalone === true;
      
      if (!isStandalone && !isIOSStandalone) {
        setCanInstall(true);
      }
    };

    checkInstallable();

    // 브라우저 자동 설치 프롬프트 캐치
    const handleBeforeInstallPrompt = (e) => {
      console.log('🎉 PWA 자동 설치 가능!');
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // 설치 완료 이벤트
    const handleAppInstalled = () => {
      console.log('✅ PWA 설치 완료!');
      setDeferredPrompt(null);
      setCanInstall(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // 원클릭 설치 함수
  const handleInstallClick = async () => {
    if (deferredPrompt) {
      console.log('🚀 직접 설치 시작!');
      deferredPrompt.prompt();
      
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`📱 사용자 선택: ${outcome}`);
      
      if (outcome === 'accepted') {
        console.log('✅ 사용자가 설치 승인!');
      } else {
        console.log('❌ 사용자가 설치 거부');
      }
      
      setDeferredPrompt(null);
    } else {
      setShowGuide(true);
    }
  };

  // 🔧 데스크톱에서는 PWA 가이드 완전히 숨기기 (모바일 전용)
  if (deviceType === 'desktop-chrome' || deviceType === 'desktop-other') {
    return null; // 모든 데스크톱 환경에서 숨김
  }

  if (!canInstall) return null;

  return (
    <>
      {/* 🆕 우측 상단 간단한 버튼 */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40">
        <button
          onClick={handleInstallClick}
          className={`
            px-4 py-2 rounded-full shadow-lg transition-all duration-300 hover:scale-105
            ${deferredPrompt 
              ? 'bg-green-500 hover:bg-green-600 text-white animate-pulse' 
              : darkMode 
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }
            font-bold text-sm
          `}
        >
          <span className="flex items-center">
            📱 <span className="ml-2">홈화면추가</span>
          </span>
        </button>
      </div>

      {/* 설치 가이드 모달 */}
      {showGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`
            max-w-md w-full rounded-2xl p-6 relative
            ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}
          `}>
            {/* 닫기 버튼 */}
            <button
              onClick={() => setShowGuide(false)}
              className="absolute top-4 right-4 text-2xl hover:opacity-70"
            >
              ✕
            </button>

            {/* 제목 */}
            <div className="text-center mb-6">
              <div className="text-4xl mb-2">📱</div>
              <h2 className="text-xl font-bold">홈 화면에 추가하기</h2>
              <p className="text-sm opacity-75 mt-2">
                앱처럼 사용하고 빠르게 접근하세요!
              </p>
            </div>

            {/* iOS 가이드 - 실제 테스트 반영 버전 */}
            {deviceType === 'ios' && (
              <div className={`p-4 rounded-xl mb-4 ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <h3 className="font-bold mb-3 text-blue-600">🍎 아이폰 설치 방법</h3>

                {/* Chrome으로 열기 안내 (우선 추천) */}
                <div className={`p-4 rounded-lg mb-4 ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-100'}`}>
                  <h4 className="font-bold text-green-600 mb-2">🌐 더 쉬운 방법 (추천!)</h4>
                  <p className="text-sm mb-3">Chrome 브라우저에서 열면 홈화면 추가가 훨씬 쉬워요!</p>
                  <button
                    onClick={() => {
                      const currentUrl = window.location.href;
                      const chromeUrl = `googlechrome://${currentUrl}`;
                      window.location.href = chromeUrl;
                      // 크롬이 없으면 앱스토어로
                      setTimeout(() => {
                        window.open('https://apps.apple.com/kr/app/google-chrome/id535886823');
                      }, 1000);
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors"
                  >
                    🌐 Chrome으로 열기
                  </button>
                  <div className="mt-3 p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-600 font-bold mb-1">Chrome에서 설치하는 방법:</p>
                    <p className="text-xs text-gray-600">
                      1️⃣ <strong>주소창 오른쪽</strong>의 공유 버튼 📤 터치<br/>
                      2️⃣ <strong>리스트에서 "홈 화면에 추가"</strong> 바로 선택<br/>
                      3️⃣ <strong>"추가"</strong> 터치하면 완료! 🎉
                    </p>
                  </div>
                </div>

                {/* Safari에서 하는 방법 */}
                <div className={`p-4 rounded-lg mb-3 ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
                  <h4 className="font-bold text-blue-600 mb-2">🔵 Safari에서 하는 방법</h4>
                  
                  {/* 카카오톡에서 온 경우 */}
                  <div className={`p-3 rounded-lg mb-3 ${darkMode ? 'bg-yellow-900 bg-opacity-30' : 'bg-yellow-100'}`}>
                    <p className="text-xs text-yellow-600 mb-2">📱 카카오톡 링크로 들어오셨나요?</p>
                    <p className="text-sm">
                      1. 하단 오른쪽 <strong>공유 아이콘 📤</strong> 터치<br/>
                      2. <strong>"Safari로 열기"</strong> 선택<br/>
                      3. Safari에서 열린 후 아래 방법 따라하기
                    </p>
                  </div>
                  
                  {/* iPhone 화면 모형 */}
                  <div className="flex justify-center mb-3">
                    <div className="relative">
                      {/* iPhone 외곽 */}
                      <div className="w-32 h-56 bg-gray-800 rounded-xl p-1">
                        {/* iPhone 화면 */}
                        <div className="w-full h-full bg-white rounded-lg relative overflow-hidden">
                          {/* 상단바 */}
                          <div className="h-6 bg-gray-100 flex items-center justify-between px-2">
                            <span className="text-xs">9:41</span>
                            <div className="flex space-x-1">
                              <div className="w-1 h-1 bg-gray-400 rounded"></div>
                              <div className="w-1 h-1 bg-gray-400 rounded"></div>
                              <div className="w-1 h-1 bg-gray-400 rounded"></div>
                            </div>
                          </div>
                          
                          {/* 주소창 */}
                          <div className="h-8 bg-gray-50 flex items-center px-2 border-b">
                            <div className="w-full h-4 bg-gray-200 rounded text-xs"></div>
                          </div>
                          
                          {/* 콘텐츠 영역 */}
                          <div className="flex-1 bg-gradient-to-b from-blue-50 to-purple-50"></div>
                          
                          {/* 하단 툴바 */}
                          <div className="h-10 bg-gray-100 flex items-center justify-center px-2 relative">
                            {/* 공유 버튼 - 중앙에 강조 */}
                            <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center relative">
                              <span className="text-white text-xs">📤</span>
                              {/* 빨간 동그라미 */}
                              <div className="absolute -top-1 -right-1 w-3 h-3 border-2 border-red-500 rounded-full animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-center mb-3">
                    Safari에서 <strong>하단 중앙의 공유 버튼 📤</strong>을 터치하세요!
                  </p>
                </div>
                
                <ol className="text-sm space-y-2">
                  <li>1️⃣ Safari 하단 중앙의 <strong>공유 버튼 📤</strong> 터치</li>
                  <li>2️⃣ 아래에서 올라오는 메뉴에서 <strong>"홈 화면에 추가"</strong> 찾아서 터치</li>
                  <li>3️⃣ 앱 이름을 <strong>"면접톡"</strong>으로 확인 후 <strong>"추가"</strong> 터치</li>
                  <li>4️⃣ 홈 화면에서 면접톡 아이콘 확인! 🎉</li>
                </ol>
              </div>
            )}

            {/* Android 가이드 */}
            {deviceType === 'android' && (
              <div className={`p-4 rounded-xl mb-4 ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                <h3 className="font-bold mb-3 text-green-600">🤖 안드로이드 설치 방법</h3>
                <div className={`p-3 rounded-lg mb-3 ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-100'}`}>
                  <p className="text-xs text-green-600 mb-2">📍 메뉴 버튼 위치:</p>
                  <p className="text-sm">Chrome 브라우저 화면 <strong>우상단</strong>에 있는 점 3개 버튼(⋮)이에요!</p>
                </div>
                <ol className="text-sm space-y-3">
                  <li>1️⃣ 화면 <strong>우상단의 점 3개 메뉴 버튼 ⋮</strong> 터치
                    <div className="text-xs text-gray-500 mt-1">💡 주소창 오른쪽 끝에 있어요</div>
                  </li>
                  <li>2️⃣ 메뉴에서 <strong>"홈 화면에 추가"</strong> 또는 <strong>"앱 설치"</strong> 터치
                    <div className="text-xs text-gray-500 mt-1">💡 스크롤해서 찾아보세요</div>
                  </li>
                  <li>3️⃣ 팝업에서 <strong>"설치"</strong> 또는 <strong>"추가"</strong> 버튼 터치</li>
                  <li>4️⃣ 홈 화면에서 면접톡 아이콘 확인! 🎉</li>
                </ol>
              </div>
            )}

            {/* 일반 기기 가이드 (모바일만) */}
            {deviceType !== 'ios' && deviceType !== 'android' && deviceType !== 'desktop-chrome' && deviceType !== 'desktop-other' && (
              <div className={`p-4 rounded-xl mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">📱 설치 방법</h3>
                <p className="text-sm">
                  브라우저 메뉴에서 <strong>"홈 화면에 추가"</strong> 또는 <strong>"앱 설치"</strong> 옵션을 찾아보세요!
                </p>
              </div>
            )}

            {/* 설치의 장점 */}
            <div className={`p-4 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="font-bold mb-2 text-green-600">✨ 설치하면 좋은 점</h3>
              <ul className="text-sm space-y-1">
                <li>• 🚀 앱처럼 빠른 실행</li>
                <li>• 📱 홈 화면에서 바로 접근</li>
                <li>• 🎯 전체화면으로 집중 가능</li>
                <li>• 💾 오프라인에서도 일부 기능 사용</li>
              </ul>
            </div>

            {/* 닫기 버튼 */}
            <button
              onClick={() => setShowGuide(false)}
              className={`
                w-full mt-4 py-3 rounded-xl font-bold transition-colors duration-300
                ${darkMode 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                }
              `}
            >
              확인했어요! 👍
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default PWAInstallGuide;